import React from 'react'
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import { CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const TYPE_COLORS = {
  loi: '#1976d2',
  dahir: '#7b1fa2',
  decret: '#0097a7',
  arrete: '#f57c00',
  circulaire: '#5c6bc0',
}

const VALIDATION_LABELS = {
  pending: { label: 'pending', color: 'warning' },
  approved: { label: 'approved', color: 'success' },
  rejected: { label: 'rejected', color: 'default' },
  previewed: { label: 'preview ready', color: 'info' },
  confirmed: { label: 'confirmed', color: 'success' },
  preview_failed: { label: 'preview failed', color: 'error' },
  executed: { label: 'executed', color: 'success' },
  failed: { label: 'failed', color: 'error' },
}

export default function BODocumentRow({ doc, onApprove, onReject, onPreview }) {
  const { t } = useTranslation()

  const status = doc.validation_status || 'pending'
  const statusConfig = VALIDATION_LABELS[status] || { label: status, color: 'default' }
  const isApproved = ['approved', 'previewed', 'confirmed', 'executed'].includes(status)
  const isRejected = status === 'rejected'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 1.5,
        borderLeft: '3px solid',
        borderLeftColor: isApproved ? '#4caf50' : isRejected ? '#e0e0e0' : 'transparent',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        opacity: isRejected ? 0.5 : 1,
        transition: 'all 0.15s ease',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.015)' },
        '&:last-child': { borderBottom: 0 },
      }}
    >
      {/* Title */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          onClick={onPreview}
          sx={{
            fontWeight: 500,
            cursor: 'pointer',
            textDecoration: isRejected ? 'line-through' : 'none',
            color: isRejected ? 'text.disabled' : 'text.primary',
            '&:hover': { color: '#c9a96e' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.title || t('du_bo_untitled', 'Untitled Document')}
        </Typography>
      </Box>

      {/* Doc type chip */}
      {doc.doc_type && (
        <Chip
          label={doc.doc_type}
          size="small"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 500,
            backgroundColor: TYPE_COLORS[doc.doc_type?.toLowerCase()] || '#757575',
            color: '#fff',
            height: 22,
          }}
        />
      )}

      {/* Doc number */}
      {doc.doc_number && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>
          {doc.doc_number}
        </Typography>
      )}

      {/* Validation status chip */}
      <Chip
        label={t(`du_bo_val_${status}`, statusConfig.label)}
        size="small"
        color={statusConfig.color}
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 22, minWidth: 90 }}
      />

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={t('du_bo_approve', 'Approve')}>
          <IconButton
            size="small"
            onClick={onApprove}
            sx={{ color: isApproved ? '#4caf50' : '#bdbdbd', '&:hover': { color: '#4caf50' } }}
          >
            <CheckCircle size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('du_bo_reject', 'Reject')}>
          <IconButton
            size="small"
            onClick={onReject}
            sx={{ color: isRejected ? '#f44336' : '#bdbdbd', '&:hover': { color: '#f44336' } }}
          >
            <XCircle size={18} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
